import type { ChoiceStep, PickOption, Scalar } from '../protocol/ui';
import type { ObjectView } from './table';
import { validateRecord } from './record';

export interface PartitionMember { value: Scalar; object: ObjectView; label: string }
export interface PartitionDomain { step: Extract<ChoiceStep, { type: 'record' }>; members: PartitionMember[] }
export interface LocalGroup { key: number; values: Scalar[] }

/** A presentation specialization only. Ordered or ambiguous records retain the ordinary editor. */
export function partitionDomain(step: ChoiceStep | undefined, objects: ObjectView[]): PartitionDomain | null {
  if (step?.type !== 'record' || step.form.type !== 'array' || step.form.ordered || step.form.items.type !== 'array' || step.form.items.ordered || step.form.items.items.type !== 'pick') return null;
  const options: PickOption[] = step.form.items.items.options;
  if (!options.length || options.length > 64 || new Set(options.map(o => o.value)).size !== options.length) return null;
  const members: PartitionMember[] = [];
  for (const option of options) {
    const matches = objects.filter(o => o.id === option.objectId && !o.faceDown);
    if (matches.length !== 1) return null;
    members.push({ value: option.value, object: matches[0]!, label: option.label });
  }
  if (new Set(members.map(m => m.object.id)).size !== members.length || partitionProblem(step, step.initial, members.map(m => m.value))) return null;
  return { step, members };
}

export function partitionProblem(step: Extract<ChoiceStep, { type: 'record' }>, groups: unknown, domain: Scalar[]): string | null {
  const problem = validateRecord(step.form, groups);
  if (problem) return problem;
  const values = (groups as Scalar[][]).flat();
  return values.length === domain.length && new Set(values).size === values.length && domain.every(v => values.includes(v)) ? null : 'Include every member exactly once.';
}

export function localGroups(groups: Scalar[][]): LocalGroup[] { return groups.map((values, key) => ({ key, values: [...values] })); }
export function partitionValues(groups: LocalGroup[]): Scalar[][] { return groups.map(g => [...g.values]); }

/** Moving explicitly selected members conserves their domain; keys never enter commands. */
export function movePartition(groups: LocalGroup[], selected: Scalar[], destination: number | 'new' | 'separate', nextKey: number): { groups: LocalGroup[]; nextKey: number } {
  const domain = groups.flatMap(g => g.values), chosen = domain.filter(v => selected.includes(v));
  if (!chosen.length || (typeof destination === 'number' && !groups.some(g => g.key === destination))) return { groups, nextKey };
  const result = groups.map(g => ({ key: g.key, values: g.values.filter(v => !chosen.includes(v)) }));
  if (destination === 'separate') for (const value of chosen) result.push({ key: nextKey++, values: [value] });
  else if (destination === 'new') result.push({ key: nextKey++, values: chosen });
  else result.find(g => g.key === destination)!.values.push(...chosen);
  return { groups: result.filter(g => g.values.length), nextKey };
}

export function partitionBody(object: ObjectView, source: 'field' | 'catalog-text' | 'fields', field?: string, fields?: readonly string[]): string | null {
  if (object.faceDown) return null;
  if (source === 'fields') {
    if (!fields?.length || fields.length > 8 || new Set(fields).size !== fields.length || fields.some(k => !/^[A-Za-z0-9][A-Za-z0-9:_.-]{0,127}$/.test(k))) return null;
    const parts = fields.map(k => object.fields[k]);
    if (parts.some(v => typeof v !== 'string' || v.length > 256)) return null;
    const joined = (parts as string[]).join('');
    return joined.length <= 2048 ? joined : null;
  }
  const value = source === 'catalog-text' ? object.catalog?.text : field ? object.fields[field] : undefined;
  return typeof value === 'string' && value.length <= (source === 'catalog-text' ? 2048 : 256) ? value : null;
}
